import React, { Component } from 'react';
import { compose } from 'redux';
import { reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { Container, H4, H5, Button, Spinner } from '../../components/index.js';
import { displayMessage } from '../../util/index.js';
import { createServiceAction } from '../../util/actionHelper.js';
import { 
    selectUploadTrainingMaterialLoading, 
    selectUploadTrainingMaterialData, 
    selectUploadTrainingMaterialError 
} from '../../reducers/trainingReducer.js';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

class TrainingMaterialAdmin extends Component {
    constructor(props) {
        super(props);
        this.state = {
            trainingMaterialFile: null,
            trainingMaterialFileName: '',
            uploadProgress: '',
            version: '',
            s3Uploading: false
        };
        this.trainingMaterialFileInputRef = React.createRef();
    }

    componentDidUpdate(prevProps) {
        const { uploadData, uploadError } = this.props;
        
        // Handle successful API response - now upload to S3
        if (uploadData && uploadData.uploadUrl && !prevProps.uploadData) {
            this.uploadToS3(uploadData.uploadUrl);
        }
        
        // Handle API error
        if (uploadError && !prevProps.uploadError) {
            displayMessage('error', uploadError.message || 'Failed to create record', 5000);
            this.setState({ uploadProgress: '' });
        }
    }

    componentWillUnmount() {
        const { resetUploadState } = this.props;
        resetUploadState();
    }

    handleTrainingMaterialFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                alert('Please select a PDF file');
                return;
            }
            if (file.size > MAX_FILE_SIZE) {
                alert('File size must be less than 25MB');
                return;
            }
            this.setState({
                trainingMaterialFile: file,
                trainingMaterialFileName: file.name
            });
        }
    }

    handleVersionChange = (e) => {
        this.setState({ version: e.target.value });
    }

    handleUploadTrainingMaterial = () => {
        const { trainingMaterialFile, trainingMaterialFileName, version } = this.state;

        if (!trainingMaterialFile) {
            alert('Please select a file to upload');
            return;
        }

        if (!version.trim()) {
            alert('Please enter version');
            return;
        }

        this.setState({ uploadProgress: 'Creating record...' });

        // Dispatch Redux action to create record and get presigned URL
        const { uploadTrainingMaterialRequest } = this.props;
        uploadTrainingMaterialRequest({
            fileName: trainingMaterialFileName,
            version: version.trim(),
            fileSize: trainingMaterialFile.size
        });
    }

    uploadToS3 = async (uploadUrl) => {
        const { trainingMaterialFile } = this.state;

        this.setState({ uploadProgress: 'Uploading to S3...', s3Uploading: true });

        try {
            const s3Response = await fetch(uploadUrl, {
                method: 'PUT',
                body: trainingMaterialFile,
                headers: {
                    'Content-Type': 'application/pdf'
                }
            });

            if (!s3Response.ok) {
                throw new Error('Failed to upload file to S3');
            }

            displayMessage('success', 'Training material uploaded successfully', 5000);
            this.resetForm();
        } catch (error) {
            console.error('Error uploading to S3:', error);
            displayMessage('error', error.message || 'Error uploading file to S3', 5000);
        } finally {
            const { resetUploadState } = this.props;
            this.setState({ s3Uploading: false, uploadProgress: '' });
            resetUploadState();
        }
    }

    resetForm = () => {
        this.setState({
            trainingMaterialFile: null,
            trainingMaterialFileName: '',
            uploadProgress: '',
            version: ''
        });

        if (this.trainingMaterialFileInputRef.current) {
            this.trainingMaterialFileInputRef.current.value = '';
        }
    }

    isFormValid = () => {
        const { trainingMaterialFile, version } = this.state;
        return trainingMaterialFile && version.trim();
    }

    isUploading = () => {
        const { uploadLoading } = this.props;
        const { s3Uploading } = this.state;
        return uploadLoading || s3Uploading;
    }

    render() {
        const { trainingMaterialFileName, uploadProgress, version } = this.state;
        const isUploading = this.isUploading();

        return (
            <div>
                {isUploading && <Spinner large text={uploadProgress || 'Uploading...'} />}
                <Container section className="ml1-sm mr1-sm mt2-sm tab-class-container tab-class padding-0">
                    <div className="ncss-col-sm-12 ncss-input-container">
                        <div className="ncss-col-sm-12 align-left">
                            <div className="pt3-sm pb3-sm">
                                <div className="p2-sm">
                                    <H4 className="text-color-accent u-bold">TRAINING MATERIALS</H4>
                                </div>
                                <div className="ncss-col-sm-12 p2-bottom-sm">
                                    <H5 className="u-bold description">1. Upload New Training PDF</H5>
                                </div>
                                <div className="ncss-col-sm-12 ncss-input-container">
                                    <H5 className="ncss-col-sm-3 u-bold description p2-top-sm p2-bottom-sm">Version</H5>
                                    <div className="ncss-col-sm-9">
                                        <input
                                            type="text"
                                            className="ncss-input pt2-sm pr4-sm pb2-sm pl4-sm"
                                            placeholder="Enter version (e.g., 2026.1)"
                                            value={version}
                                            onChange={this.handleVersionChange}
                                        />
                                    </div>
                                </div>
                                <div className="ncss-col-sm-12 ncss-input-container p2-top-sm">
                                    <H5 className="ncss-col-sm-3 u-bold description p2-top-sm p2-bottom-sm">PDF File</H5>
                                    <div className="ncss-col-sm-9">
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            ref={this.trainingMaterialFileInputRef}
                                            onChange={this.handleTrainingMaterialFileChange}
                                            style={{ marginBottom: '5px' }}
                                        />
                                        <div className="text-color-secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                                            {trainingMaterialFileName ? trainingMaterialFileName : 'No file selected (Max 25MB)'}
                                        </div>
                                    </div>
                                </div>
                                <div className="ncss-col-sm-12 ncss-input-container p2-top-sm">
                                    <div className="ncss-col-sm-3"></div>
                                    <div className="ncss-col-sm-9">
                                        <Button
                                            className={`ncss-btn-primary-dark u-bold submit-button-enable new-material add-supplier-location-buttton ${!this.isFormValid() || isUploading ? 'disabled' : ''}`}
                                            onClick={this.handleUploadTrainingMaterial}
                                            disabled={!this.isFormValid() || isUploading}
                                        >
                                            {isUploading ? uploadProgress || 'Uploading...' : 'Upload'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    uploadLoading: selectUploadTrainingMaterialLoading(state),
    uploadData: selectUploadTrainingMaterialData(state),
    uploadError: selectUploadTrainingMaterialError(state)
});

const mapDispatchToProps = dispatch => ({
    uploadTrainingMaterialRequest: data => dispatch(createServiceAction('uploadTrainingMaterial', 'requested')(data)),
    resetUploadState: () => dispatch({ type: 'UPLOADTRAININGMATERIAL_RESET' })
});

export default compose(reduxForm({ form: 'trainingMaterialAdmin' }),
    connect(
        mapStateToProps,
        mapDispatchToProps
    ))(TrainingMaterialAdmin);
